import { useEffect, useState } from "react";
import type { Patient, ProcedureCode } from "@dentacore/shared-types";

import { searchPatients } from "../api/patients";
import styles from "./calendar.module.css";
import own from "./family-members.module.css";

/** One more patient in the same visit, and what they are coming for. */
export interface FamilyMember {
  patient: Patient;
  /** What they are coming for, in order — none is "not decided yet". */
  procedureCodeIds: string[];
}

/**
 * **Several procedures for one person, as tags on one line** (Emile,
 * 2026-10-22: *"let the procedure come as tags"*, then *"add 2 then have a show
 * more button"*). Two tags show; the rest fold into a "+N" that opens them all
 * and closes them again, so a long list never grows the form. The picker is a
 * small "+ Add" pill at the end of the line, and a procedure already chosen is
 * not offered again. Every change reports the minutes it adds or removes, so
 * *To* follows.
 */
export function ProcedureTags({
  values,
  onChange,
  catalogue,
  who,
  addLabel = "+ Add",
}: {
  values: readonly string[];
  onChange: (next: string[], minutes: number) => void;
  catalogue: readonly ProcedureCode[];
  who: string;
  addLabel?: string;
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const codeOf = (id: string): ProcedureCode | undefined =>
    catalogue.find((code) => code.id === id);
  const left = catalogue.filter((code) => !values.includes(code.id));
  const shown = expanded ? values : values.slice(0, 2);
  const hidden = values.length - shown.length;
  return (
    <div
      className={expanded ? `${own.tags} ${own.tagsOpen}` : own.tags}
      data-testid="book-procedure-tags"
    >
      {shown.map((id) => (
        <span
          key={id}
          className={own.tag}
          title={codeOf(id)?.label}
          data-testid="book-procedure-tag"
        >
          <span className={own.tagText}>{codeOf(id)?.label ?? "Procedure"}</span>
          <button
            type="button"
            className={own.tagX}
            aria-label={`Remove ${codeOf(id)?.label ?? "this procedure"} for ${who}`}
            onClick={() =>
              onChange(
                values.filter((other) => other !== id),
                -(codeOf(id)?.defaultDurationMinutes ?? 0),
              )
            }
          >
            ×
          </button>
        </span>
      ))}
      {(hidden > 0 || (expanded && values.length > 2)) && (
        <button
          type="button"
          className={own.moreTags}
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          data-testid="book-procedure-more"
        >
          {expanded ? "Show less" : `+${String(hidden)} more`}
        </button>
      )}
      {left.length > 0 && (
        <select
          className={own.addPill}
          value=""
          aria-label={`Add a procedure for ${who}`}
          onChange={(event) => {
            const code = codeOf(event.target.value);
            if (code !== undefined) onChange([...values, code.id], code.defaultDurationMinutes);
          }}
          data-testid="book-procedure-add"
        >
          <option value="">
            {values.length === 0 && addLabel === "+ Add" ? "+ What for" : addLabel}
          </option>
          {left.map((code) => (
            <option key={code.id} value={code.id}>
              {code.label} · {code.defaultDurationMinutes} min
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/**
 * **Several patients in one visit** — a parent bringing three children, seen
 * one after another in the same chair (FV-D1–FV-D3, Emile, 2026-10-22).
 *
 * Folded behind one link until it is wanted, so the ordinary one-patient
 * booking reads exactly as before. Opened, it suggests the people who share the
 * first patient's phone or family name — a family's children are usually on a
 * parent's number — and searches like the Patient field above it.
 *
 * Each one keeps their own procedures, because each child has their own chart
 * and their own bill. Picking one moves *To* on by its length, the same way the
 * first patient's procedure sets it; *To* stays editable for a family seen at
 * once rather than in turn.
 */
export function FamilyMembers({
  first,
  firstProcedureCodeId,
  members,
  onChange,
  catalogue,
  onLength,
  onNewPatient,
  canCreate,
}: {
  first: Patient;
  firstProcedureCodeId: string | null;
  members: readonly FamilyMember[];
  onChange: (next: FamilyMember[]) => void;
  catalogue: readonly ProcedureCode[];
  /** Minutes to move *To* by — positive when someone's procedure adds time. */
  onLength: (minutes: number) => void;
  onNewPatient: () => void;
  canCreate: boolean;
}): React.JSX.Element | null {
  const [open, setOpen] = useState(members.length > 0);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<Patient[]>([]);
  const [family, setFamily] = useState<Patient[]>([]);

  const taken = new Set([first.id, ...members.map((member) => member.patient.id)]);
  const lengthOf = (codeId: string | null): number =>
    catalogue.find((code) => code.id === codeId)?.defaultDurationMinutes ?? 0;

  /**
   * **The family is whoever shares the first patient's number** (FV-D8) — how
   * a household is already recorded. Not the family name, which suggested
   * strangers: a common one is half the waiting room.
   */
  useEffect(() => {
    const controller = new AbortController();
    const phone = first.phoneE164;
    if (phone === "") {
      setFamily([]);
      return;
    }
    searchPatients({ q: phone, limit: 8 }, controller.signal)
      .then((page) =>
        setFamily(page.patients.filter((p) => p.id !== first.id && p.phoneE164 === phone)),
      )
      .catch(() => setFamily([]));
    return () => controller.abort();
  }, [first.id, first.phoneE164]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setMatches([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      searchPatients({ q: query, limit: 3 }, controller.signal)
        .then((page) => setMatches(page.patients))
        .catch(() => setMatches([]));
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const add = (patient: Patient): void => {
    // They come for what the first patient comes for, until told otherwise —
    // unless that is one this picker does not offer, a multi-visit one (FV-D18).
    const same =
      firstProcedureCodeId !== null && catalogue.some((code) => code.id === firstProcedureCodeId)
        ? firstProcedureCodeId
        : null;
    onChange([...members, { patient, procedureCodeIds: same === null ? [] : [same] }]);
    onLength(lengthOf(same));
    setQuery("");
    setMatches([]);
  };

  /**
   * **Loud only for a patient with family on file** (FV-D9, Emile: *"hide this
   * unless the patient has a family member … make it pop"*): someone on the same
   * number makes it as solid as *Book*.
   */
  if (!open) {
    // **No one on the same number: a quiet link** (FV-D9), so a child on
    // another number can still be added by search without the form shouting.
    if (family.length === 0) {
      return (
        <button
          type="button"
          className={styles.linkish}
          onClick={() => setOpen(true)}
          data-testid="book-family-open"
        >
          + Add another patient
        </button>
      );
    }
    return (
      <button
        type="button"
        className={`${styles.primary} ${own.add}`}
        onClick={() => setOpen(true)}
        data-testid="book-family-open"
      >
        + Add family to this visit ({family.length} on file)
      </button>
    );
  }

  const suggestions = family.filter((p) => !taken.has(p.id)).slice(0, 4);

  return (
    <div className={own.family} data-testid="book-family">
      <span className={styles.fieldLabel}>Also in this visit</span>

      {members.map((member, index) => (
        <div key={member.patient.id} className={own.member} data-testid="book-family-member">
          <span className={own.memberName}>
            <span>
              {member.patient.firstName} {member.patient.lastName}
            </span>
            <span className={own.file}>{member.patient.fileNumberDisplay}</span>
          </span>
          {catalogue.length > 0 && (
            <ProcedureTags
              values={member.procedureCodeIds}
              catalogue={catalogue}
              who={member.patient.firstName}
              onChange={(next, minutes) => {
                onLength(minutes);
                onChange(
                  members.map((other, at) =>
                    at === index ? { ...other, procedureCodeIds: next } : other,
                  ),
                );
              }}
            />
          )}
          <button
            type="button"
            className={own.remove}
            aria-label={`Take ${member.patient.firstName} out of this visit`}
            onClick={() => {
              onLength(-member.procedureCodeIds.reduce((sum, id) => sum + lengthOf(id), 0));
              onChange(members.filter((_, at) => at !== index));
              // The last one out folds the box back to its button (Emile, 2026-10-22).
              if (members.length === 1) setOpen(false);
            }}
            data-testid="book-family-remove"
          >
            ×
          </button>
        </div>
      ))}

      {suggestions.length > 0 && (
        <div className={own.suggest}>
          <span className={styles.hint}>On {first.firstName}&rsquo;s number</span>
          <div className={styles.matches} data-testid="book-family-suggestions">
            {suggestions.map((p) => (
              <button
                key={p.id}
                type="button"
                className={styles.match}
                onClick={() => add(p)}
                data-testid="book-family-suggestion"
              >
                + {p.firstName} {p.lastName}
              </button>
            ))}
          </div>
        </div>
      )}

      <input
        className={styles.input}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Add someone else — name, phone or file number"
        aria-label="Find another patient for this visit"
        data-testid="book-family-search"
      />
      {matches.filter((p) => !taken.has(p.id)).length > 0 && (
        <div className={styles.matches}>
          {matches
            .filter((p) => !taken.has(p.id))
            .map((p) => (
              <button
                key={p.id}
                type="button"
                className={styles.match}
                onClick={() => add(p)}
                data-testid="book-family-match"
              >
                {p.firstName} {p.lastName} · {p.fileNumberDisplay}
              </button>
            ))}
        </div>
      )}
      {canCreate && (
        <button type="button" className={styles.linkish} onClick={onNewPatient}>
          New patient — not on file yet
        </button>
      )}
    </div>
  );
}
